import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Popconfirm, Space, Steps, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../../../shared/i18n';
import domainRegistrationService from '../../../../services/domainRegistrationService';

const { Text } = Typography;

// 只在转入中 / 转入失败 / 已取消时显示
const SHOW_FOR = ['pending', 'processing', 'failed', 'cancelled'];

const SUB_HINT = {
    waiting_owner: '请留意原注册商发到域名邮箱的确认邮件，确认后会更快',
    waiting_registrar: '注册商处理中',
    waiting_registry: '注册局处理中，通常 5–7 天内完成',
    verifying: '正在核实注册商结果，不会重复扣费',
    submitted: '已提交给注册商',
    retrying: '注册商暂时繁忙，系统会自动重试',
};

/**
 * 域名转入进度：已提交 → 等待原注册商确认 → 完成 / 失败（白话原因）/ 已取消；支持时可「取消转入」。
 */
const TransferProgress = ({ domain, onChange }) => {
    const { t } = useTranslation();
    const [st, setSt] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const show = domain && SHOW_FOR.includes(domain.status);

    const load = useCallback(async () => {
        if (!show) return;
        setLoading(true);
        try {
            const r = await domainRegistrationService.getTransferStatus(domain.id);
            setSt(r?.success ? r.data : null);
        } catch (e) {
            setSt(null);
        } finally {
            setLoading(false);
        }
    }, [domain?.id, show]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    if (!show || !st) return null;

    const cancel = async () => {
        setCancelling(true);
        try {
            const r = await domainRegistrationService.cancelTransfer(domain.id);
            if (r?.success) {
                message.success(t('Transfer cancelled. The fee is back in your balance'));
                setSt(r.data || null);
                onChange && onChange();
            } else {
                message.error(r?.error || t('Cancellation failed'));
            }
        } catch (e) {
            message.error(e?.error || e?.message || t('Cancellation failed'));
            load();
        } finally {
            setCancelling(false);
        }
    };

    const stepIndex = { awaiting_payment: 0, submitted: 0, waiting: 1, completed: 2, failed: st.initiated_at ? 1 : 0, cancelled: st.initiated_at ? 1 : 0 }[st.stage] ?? 0;
    const stepStatus = st.stage === 'completed' ? 'finish' : (['failed', 'cancelled'].includes(st.stage) ? 'error' : 'process');
    const hint = st.stage === 'awaiting_payment' ? t('Awaiting payment')
        : (st.sub_stage && SUB_HINT[st.sub_stage] ? t(SUB_HINT[st.sub_stage]) : null);

    return (
        <Card
            bordered={false}
            style={{ marginBottom: 16 }}
            title={t('Transfer progress')}
            extra={(
                <Space>
                    {st.can_cancel && (
                        <Popconfirm title={t('Cancel the transfer? The fee goes back to your balance')} okText={t('Cancel transfer')} cancelText={t('Keep it')} okButtonProps={{ danger: true }} onConfirm={cancel}>
                            <Button danger size="small" loading={cancelling}>{t('Cancel transfer')}</Button>
                        </Popconfirm>
                    )}
                    <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={load} />
                </Space>
            )}
        >
            <Steps
                size="small"
                responsive
                current={stepIndex}
                status={stepStatus}
                items={[
                    { title: t('Submitted') },
                    { title: t('Waiting for the current registrar'), description: st.stage === 'waiting' && st.eta_at ? t('Expected by {{date}}', { date: st.eta_at }) : null },
                    { title: st.stage === 'failed' ? t('Transfer failed') : (st.stage === 'cancelled' ? t('Canceled') : t('Done')) },
                ]}
            />
            {hint && !['failed', 'cancelled', 'completed'].includes(st.stage) && (
                <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>{hint}</Text>
            )}
            {st.stage === 'failed' && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginTop: 12 }}
                    message={st.failure_reason ? t(st.failure_reason) : t('Transfer failed')}
                    description={st.refunded ? t('The fee is back in your balance') : null}
                />
            )}
            {st.stage === 'cancelled' && st.refunded && (
                <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>{t('The fee is back in your balance')}</Text>
            )}
        </Card>
    );
};

export default TransferProgress;
